/*
 * @Author: Yu JiaZe
 * @Description:
 * @Date: 2019-12-25 17:39:06
 * @LastEditTime: 2021-04-08 20:58:52
 */

declare global {
    interface CssRule {
        selectorText: string;
        style: CSSStyleDeclaration;
        type: number;
        cssText: string;
        parentRule: null;
    }

    interface StyleSheet {
        cssRules: CssRule[];
        insertRule(rule: string, index?: number): void;
    }
}

type HTMLStyleOrCssLinkElement = HTMLStyleElement | HTMLLinkElement;

export enum SandboxRecordStatus {
    BOOTSTRAP,
    MOUNT,
    UNMOUNT,
}

export class SandBox {
    recordStatus: SandboxRecordStatus = SandboxRecordStatus.BOOTSTRAP;

    windowSnapshot: { [key: string]: Map<PropertyKey, any> | null } = {
        beforeBootstrap: null,
        beforeMount: null,
    };

    styleSheets: HTMLStyleOrCssLinkElement[] = [];

    // style-component 通过 insertRule 插入样式，style标签本身内容是空的，这里保存 style 标签和 cssRules 的关系
    styleComponentMap = new Map<HTMLStyleElement, CssRule[]>();

    // 基准样式，比如主APP提供的样式，在每个应用bootstrap前就产生了
    baseStyleNodeMap = new Map<HTMLElement, boolean>();

    saveBaseStyle() {
        const styleSheetsList: StyleSheet[] = [].slice.call(
            document.styleSheets
        );
        for (const style of styleSheetsList) {
            if (
                style.disabled ||
                !style.ownerNode ||
                style.type !== "text/css"
            ) {
                continue;
            }
            this.baseStyleNodeMap.set(
                style.ownerNode as HTMLStyleOrCssLinkElement,
                true
            );
        }
    }

    saveCurrentStyle(remove?: boolean) {
        this.styleSheets = [];
        const styleSheetsList: StyleSheet[] = [].slice.call(
            document.styleSheets
        );
        for (const style of styleSheetsList) {
            if (
                style.disabled ||
                !style.ownerNode ||
                style.type !== "text/css"
            ) {
                continue;
            }
            this.styleSheets.push(style.ownerNode as HTMLStyleOrCssLinkElement);
            if ((style.ownerNode as HTMLElement).tagName === "STYLE") {
                if (
                    (style.ownerNode as HTMLElement).innerText === "" &&
                    style.cssRules.length
                ) {
                    this.processScStyle(style);
                }
            }
            if (
                remove &&
                !this.baseStyleNodeMap.get(style.ownerNode as HTMLElement)
            ) {
                (style.ownerNode as HTMLElement).remove();
            }
        }
    }

    recoverStyle() {
        if (this.styleSheets.length) {
            this.styleSheets.forEach((el) => {
                if (!document.head.contains(el)) {
                    document.head.appendChild(el);
                }
            });
            const styleSheetsList: StyleSheet[] = [].slice.call(
                this.styleSheets
            );
            for (const style of styleSheetsList) {
                const el = style.ownerNode as HTMLStyleElement;
                if (this.styleComponentMap.get(el)) {
                    const cssRules: CssRule[] = [].slice.call(
                        this.styleComponentMap.get(el)
                    );
                    cssRules.forEach((css) => {
                        style.insertRule(css.cssText);
                    });
                }
            }
        }
    }

    processScStyle(style: StyleSheet) {
        this.styleComponentMap.set(
            style.ownerNode as HTMLStyleElement,
            style.cssRules
        );
    }

    createWindowSnapshot() {
        const snapshot = new Map<PropertyKey, any>();
        // 如果有不可枚举的，这里没办法感知，snapshot的缺陷之一
        for (const i in window) {
            try {
                // 只读的不用考虑
                if (this.isReadonlyProperty(i)) {
                    continue;
                }
                snapshot.set(i, window[i]);
            } catch (e) {
                console.warn("Sandbox Get Window Descriptor Error: " + i, e);
            }
        }
        return snapshot;
    }

    snapshotBeforeBoot() {
        this.windowSnapshot.beforeBootstrap = this.createWindowSnapshot();
    }

    snapshotBeforeMount() {
        this.windowSnapshot.beforeMount = this.createWindowSnapshot();
    }

    recoverWindowToBeforeMount() {
        if (this.windowSnapshot.beforeMount) {
            this.windowSnapshot.beforeMount.forEach((v, k: any) => {
                if (!window[k] || window[k] !== v) {
                    // 全盘恢复就行了
                    window[k] = v;
                }
            });
        }
    }

    isReadonlyProperty(i: PropertyKey) {
        const desc = Object.getOwnPropertyDescriptor(window, i);
        // 只读的不用考虑
        if (!desc || desc.writable === false || (desc.get && !desc.set)) {
            return true;
        }
        return false;
    }

    recoverWindowToBeforeBootstrap() {
        // modify\add\delete
        for (const i in window) {
            if (!this.isReadonlyProperty(i)) {
                // add: snapshot 里没有，并且不是只读的（只读的不会被放进 snapshot），肯定是应用新增的，所以需要删掉
                if (this.windowSnapshot.beforeBootstrap && !this.windowSnapshot.beforeBootstrap.has(i)) {
                    try {
                        (window as any)[i] = undefined;
                        delete window[i];
                    } catch (e) {
                        // 这里如果 i 是只读的，也许会报错？
                        console.error(e);
                    }
                    continue;
                }
                // modify: 和 snapshot 里不一样，说明被改了，将其恢复
                if (this.windowSnapshot.beforeBootstrap && this.windowSnapshot.beforeBootstrap.get(i) !== window[i]) {
                    (window as any)[i] = this.windowSnapshot.beforeBootstrap.get(i);
                    continue;
                }
            }
        }

        this.windowSnapshot.beforeBootstrap && this.windowSnapshot.beforeBootstrap.forEach((v, k) => {
            // delete: 现在的window里没有了，说明被删了，将其恢复
            if (!window.hasOwnProperty(k)) {
                (window as any)[k] = v;
            }
        });
    }
}
